import type { LocaleOfStoreType } from "@beautypie/core";
import { getUserMenuItems } from "@membership/navigation.account";
import { CloseIcon } from "components/SVG";
import { useClickOutside } from "hooks";
import { userVar } from "lib/user";
import React, { useEffect, useRef } from "react";

import {
  useTrackElementClicked,
  useTrackElementViewed
} from "packages/membership/tracking";

import { focusElement } from "./UserMenu.utils";
import {
  itemsContainer,
  menu,
  menuItem,
  menuItemLink,
  menuItemsContainer,
  signoutButton,
  welcomeContainer,
  welcomeText
} from "./style.css";

interface Props {
  toggleUserMenu: (id?: string) => void;
  onLogout?: () => void;
  locale: LocaleOfStoreType;
}

const baseTrackingValues = {
  target: "order_it_again_profile_menu",
  location: "navigation"
};

export const UserMenu: React.FC<Props> = ({
  locale,
  onLogout,
  toggleUserMenu
}) => {
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { isMember } = userVar();

  useClickOutside(userMenuRef, () => {
    toggleUserMenu();
  });

  const { ref } = useTrackElementViewed({ ...baseTrackingValues });
  const { elementClicked } = useTrackElementClicked({ ...baseTrackingValues });

  const menuItems = getUserMenuItems({
    isMember
  });

  useEffect(() => {
    focusElement("menuitem", 0);
  }, []);

  const handleKeyDown = (e, index) => {
    if (e.key === "ArrowDown") {
      focusElement("menuitem", index + 1);
    } else if (e.key === "ArrowUp") {
      focusElement("menuitem", index - 1);
    } else if (e.key === "Escape") {
      toggleUserMenu("desktop-login");
    } else if (e.key === "Tab") {
      toggleUserMenu(e.shiftKey ? "desktop-login" : "desktop-shopping-cart");
    }
  };

  const logoutUser = async () => {
    toggleUserMenu();
    if (onLogout) onLogout();
  };

  return (
    <div className={menu} ref={userMenuRef}>
      <div role="menu" data-testid="desktop-user-profile-menu">
        <div className={itemsContainer}>
          <div className={welcomeContainer}>
            <span className={welcomeText}>WELCOME BACK</span>
            <button
              type="button"
              data-testid="profile-menu-close-btn"
              onClick={() => toggleUserMenu()}
              tabIndex={-1}
              id="AccountMenuCloseButton"
              style={{ display: "flex" }}
            >
              <CloseIcon width={24} height={24} />
            </button>
          </div>

          <div className={menuItemsContainer}>
            {menuItems.map((item, index) => (
              <a
                className={menuItemLink}
                role="menuitem"
                key={item.slug}
                href={`${locale === "en-US" ? "/us" : ""}${item.url}`}
                data-testid={`profile-menu-${item.slug}`}
                tabIndex={-1}
                id={item.slug}
                data-menuitem={index}
                ref={item.showNotification ? ref : undefined}
                onClick={() => {
                  toggleUserMenu();
                  if (item.showNotification) {
                    elementClicked({
                      details: "order_it_again_profile_link"
                    });
                  }
                }}
                onKeyDown={(e) => {
                  handleKeyDown(e, index);
                }}
              >
                <span className={menuItem}>{item.title}</span>
              </a>
            ))}

            <button
              type="button"
              className={signoutButton}
              role="menuitem"
              data-testid="profile-menu-logout-btn"
              onClick={logoutUser}
              id="UserMenu"
              tabIndex={-1}
              data-menuitem={menuItems.length}
              onKeyDown={(e) => {
                handleKeyDown(e, menuItems.length);
              }}
            >
              SIGN OUT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
